import React, { useEffect, useState } from "react";
import { Dropdown, Button } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEllipsisVertical,
  faPen,
  faTrash,
  faUsers,
  faToggleOn,
  faToggleOff,
  faPlus,
  faEye,
  faKey,
} from "@fortawesome/free-solid-svg-icons";
import AddInvigilatorModal from "components/Modals/AddInvigilatorModal";
import EditInvigilatorModal from "components/Modals/EditInvigilatorModal";
import { InvisilatorService } from "Services/InvisilatorService";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import ResetPasswordModal from "components/Modals/ResetPasswordModal";
import DeleteInvigilatorModal from "components/Modals/DeleteInvigilatorModal";

export default function AssignInvigilatorPage() {
  const { examId } = useParams<{ examId: string }>();

  
  const [invigilators, setInvigilators] = useState<any[]>([]);

  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedInvigilator, setSelectedInvigilator] = useState<any>(null);
  const [showResetModal, setShowResetModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const navigate = useNavigate();

  /* ---------- ADD SUCCESS ---------- */

  const handleAddInvigilator = async () => {
  
    await getInvigilators();
    setShowAddModal(false);
  };

  /* ---------- UPDATE ---------- */

  const handleUpdateInvigilator = (updated: any) => {
    setInvigilators((prev) =>
      prev.map((i) =>
        i.invigilatorId === updated.invigilatorId ? updated : i,
      ),
    );
  };

  /* ---------- STATUS ---------- */

  const updateStatus = async (inv: any) => {
    try {
      const newStatus = inv.status === "ACTIVE" ? "INACTIVE" : "ACTIVE";

      const res = await InvisilatorService.updateInvigilatorStatus(inv._id, {
        status: newStatus,
      });

      if (res?.status === 200) {
        toast.success("Status updated");

        setInvigilators((prev) =>
          prev.map((i) =>
            i._id === inv._id ? { ...i, status: newStatus } : i,
          ),
        );
      }
    } catch (err) {
      toast.error("Failed to update status");
    }
  };

  /* ---------- DELETE ---------- */

  const confirmDeleteInvigilator = async () => {
    if (!selectedInvigilator) return;

    try {
      const res = await InvisilatorService.deleteInvigilator(
        selectedInvigilator._id,
      );

      if (res?.status === 200) {
        toast.success("Invigilator deleted successfully");

        setInvigilators((prev) =>
          prev.filter((i) => i._id !== selectedInvigilator._id),
        );

        setShowDeleteModal(false);
        setSelectedInvigilator(null);
      }
    } catch (err) {
      toast.error("Failed to delete invigilator");
    }
  };

  /* ---------- FETCH ---------- */

  const getInvigilators = async () => {
    if (!examId) return;

    try {
      const res = await InvisilatorService.getInvisilatorByExam(examId);

      if (res?.status === 200) {
        const list = res?.data?.data?.invigilators || [];
        setInvigilators(list);
      }
    } catch (error) {
      toast.error("Unable to fetch invigilators");
    }
  };

  useEffect(() => {
    getInvigilators();
  }, [examId]);

  /* ================= UI ================= */

  return (
    <div className="card shadow-sm p-4">
      {/* HEADER */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0">Assign Invigilator</h4>

        <Button variant="primary" onClick={() => setShowAddModal(true)}>
          <FontAwesomeIcon icon={faPlus} className="me-2" />
          Add Invigilator
        </Button>
      </div>

      {/* TABLE */}
      <div className="table-responsive">
        <table className="table table-hover align-middle">
          <thead className="table-light">
            <tr>
              <th>Invigilator ID</th>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Permissions</th>
              <th className="text-center">Students</th>
              <th>Status</th>
              {/* <th>Last Login</th> */}
              <th className="text-center">Actions</th>
            </tr>
          </thead>

          <tbody>
            {invigilators.map((inv, index) => (
              <tr key={inv._id || index}>
                <td className="fw-semibold">{inv.invigilatorId}</td>
                <td>{inv.name}</td>
                <td className="text-muted">{inv.email}</td>

                <td>
                  <span className="badge bg-secondary px-3 py-2">
                    {inv.role}
                  </span>
                </td>

                {/* FIX 2: permissions safe */}
                <td>
                  {inv.permissions
                    ? Object.entries(inv.permissions)
                        .filter(([_, v]) => v)
                        .map(([k]) => (
                          <span
                            key={k}
                            className="badge bg-success-subtle text-success border me-1 mb-1"
                          >
                            {k.replace("can", "")}
                          </span>
                        ))
                    : "-"}
                </td>

                
                <td className="text-center fw-bold">
                  {inv.assignedStudents?.length || 0}
                </td>

                <td>
                  <span
                    className={`badge px-3 py-2 fw-semibold ${
                      inv.status === "ACTIVE"
                        ? "bg-success bg-opacity-10 text-success border border-success"
                        : "bg-danger bg-opacity-10 text-danger border border-danger"
                    }`}
                  >
                    {inv.status}
                  </span>
                </td>

                {/* <td className="text-muted">{inv.lastLoginAt || "--"}</td> */}

                {/* ACTIONS */}
                <td className="text-center">
                  <Dropdown align="end">
                    <Dropdown.Toggle
                      variant="light"
                      size="sm"
                      className="border-0"
                    >
                      <FontAwesomeIcon icon={faEllipsisVertical} />
                    </Dropdown.Toggle>

                    <Dropdown.Menu>
                      <Dropdown.Item
                        onClick={() =>
                          navigate(`/admin-dash/invigilator/view/${inv._id}`)
                        }
                      >
                        <FontAwesomeIcon
                          icon={faEye}
                          className="me-2 text-secondary"
                        />
                        View
                      </Dropdown.Item>

                      <Dropdown.Item
                        onClick={() => {
                          setSelectedInvigilator(inv);
                          setShowEditModal(true);
                        }}
                      >
                        <FontAwesomeIcon
                          icon={faPen}
                          className="me-2 text-primary"
                        />
                        Edit
                      </Dropdown.Item>

                      <Dropdown.Item
                        onClick={() =>
                          navigate(`/admin-dash/assign-students/${inv._id}`, {
                            state: {
                              name: inv.name,
                              email: inv.email,
                            },
                          })
                        }
                      >
                        <FontAwesomeIcon
                          icon={faUsers}
                          className="me-2 text-info"
                        />
                        Assign Students
                      </Dropdown.Item>

                      <Dropdown.Item onClick={() => updateStatus(inv)}>
                        <FontAwesomeIcon
                          icon={
                            inv.status === "ACTIVE" ? faToggleOff : faToggleOn
                          }
                          className="me-2 text-warning"
                        />
                        Set {inv.status === "ACTIVE" ? "Inactive" : "Active"}
                      </Dropdown.Item>

                      <Dropdown.Item
                        onClick={() => {
                          setSelectedInvigilator(inv);
                          setShowResetModal(true);
                        }}
                      >
                        <FontAwesomeIcon
                          icon={faKey}
                          className="me-2 text-warning"
                        />
                        Reset Password
                      </Dropdown.Item>

                      <Dropdown.Divider />

                      <Dropdown.Item
                        className="text-danger"
                        onClick={() => {
                          setSelectedInvigilator(inv);
                          setShowDeleteModal(true);
                        }}
                      >
                        <FontAwesomeIcon icon={faTrash} className="me-2" />
                        Delete
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* MODALS */}
      <AddInvigilatorModal
        examId={examId}
        show={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSuccess={handleAddInvigilator}
      />

      <EditInvigilatorModal
        show={showEditModal}
        data={selectedInvigilator}
        onClose={() => setShowEditModal(false)}
        onUpdate={handleUpdateInvigilator}
      />

      <ResetPasswordModal
        show={showResetModal}
        invigilator={selectedInvigilator}
        onClose={() => setShowResetModal(false)}
      />

      <DeleteInvigilatorModal
        show={showDeleteModal}
        invigilator={selectedInvigilator}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmDeleteInvigilator}
      />
    </div>
  );
}
